import { getStateOptions } from '@/helpers/getStateOptions';

import {
  Clientize,
  normalizeCountyName,
  SystemLossThreshold,
  SystemLossThresholds,
  SystemLossThresholdsValue,
} from 'types';

export interface CountyFormValue {
  name: string;
  snowLossPercent: string;
  minSystemLossPercent: string;
}

export interface StateFormValue {
  stateCode: string;
  snowLossPercent: string;
  minSystemLossPercent: string;
  counties: CountyFormValue[];
}

export interface SystemLossThresholdsFormValues {
  entries: StateFormValue[];
}

export interface StateOption {
  label: string;
  value: string;
}

export const DEFAULT_FORM_VALUES: SystemLossThresholdsFormValues = {
  entries: [],
};

export const EMPTY_STATE: StateFormValue = {
  stateCode: '',
  snowLossPercent: '',
  minSystemLossPercent: '',
  counties: [],
};

export const EMPTY_COUNTY: CountyFormValue = {
  name: '',
  snowLossPercent: '',
  minSystemLossPercent: '',
};

const STATE_CODE_PATTERN = /^[A-Z]{2}$/;
const PERCENT_PATTERN = /^\d+(\.\d+)?$/;
const PERCENT_MESSAGE = 'Enter a number from 0 to 100';

const thresholdToForm = (threshold: SystemLossThreshold) => ({
  snowLossPercent: threshold.snowLossPercent === null ? '' : String(threshold.snowLossPercent),
  minSystemLossPercent: String(threshold.minSystemLossPercent),
});

export const toFormValues = (doc: Clientize<SystemLossThresholds> | undefined): SystemLossThresholdsFormValues => {
  if (!doc) return DEFAULT_FORM_VALUES;
  return {
    entries: doc.value.entries.map((entry) => ({
      stateCode: entry.stateCode,
      ...thresholdToForm(entry),
      counties: (entry.countyOverrides ?? []).map((county) => ({
        name: county.name,
        ...thresholdToForm(county),
      })),
    })),
  };
};

// Blank snow loss means "no snow component" (Puerto Rico), which the API requires as an explicit null.
const parseSnow = (raw: string): number | null => (raw.trim() === '' ? null : Number(raw));

export const toPayload = (values: SystemLossThresholdsFormValues): SystemLossThresholdsValue => ({
  entries: values.entries.map((entry) => ({
    stateCode: entry.stateCode.trim(),
    snowLossPercent: parseSnow(entry.snowLossPercent),
    minSystemLossPercent: Number(entry.minSystemLossPercent),
    ...(entry.counties.length > 0
      ? {
          countyOverrides: entry.counties.map((county) => ({
            name: county.name.trim(),
            snowLossPercent: parseSnow(county.snowLossPercent),
            minSystemLossPercent: Number(county.minSystemLossPercent),
          })),
        }
      : {}),
  })),
});

const isPercentInRange = (raw: string): boolean => PERCENT_PATTERN.test(raw.trim()) && Number(raw) <= 100;

export const validateMinPercent = (value: string): true | string => isPercentInRange(value) || PERCENT_MESSAGE;

export const validateSnowPercent = (value: string): true | string =>
  value.trim() === '' || isPercentInRange(value) || PERCENT_MESSAGE;

export const STATE_OPTIONS: StateOption[] = getStateOptions();

export const getUsedStateCodes = (entries: StateFormValue[]): Set<string> =>
  new Set(entries.map((entry) => entry.stateCode.trim()).filter(Boolean));

export const areAllStatesUsed = (entries: StateFormValue[]): boolean => {
  const used = getUsedStateCodes(entries);
  return STATE_OPTIONS.every((option) => used.has(option.value));
};

// Hides states claimed by other rows but always keeps this row's own value, labeling a code that is no longer in the list by the code itself.
export const getStateOptionsForRow = (entries: StateFormValue[], index: number): StateOption[] => {
  const ownCode = entries[index]?.stateCode.trim() ?? '';
  const usedByOthers = getUsedStateCodes(entries.filter((_, i) => i !== index));
  const options = STATE_OPTIONS.filter((option) => !usedByOthers.has(option.value));
  const isLegacyCode = ownCode !== '' && !STATE_OPTIONS.some((option) => option.value === ownCode);
  return isLegacyCode ? [{ label: ownCode, value: ownCode }, ...options] : options;
};

export const validateStateCode = (value: string, entries: StateFormValue[], index: number): true | string => {
  const code = value.trim();
  if (!code) return 'Select a state';
  if (!STATE_CODE_PATTERN.test(code)) return 'Use a two-letter uppercase code';
  const isDuplicate = entries.some((entry, i) => i !== index && entry.stateCode.trim() === code);
  return !isDuplicate || 'Duplicate state';
};

export const validateCountyName = (value: string, counties: CountyFormValue[], index: number): true | string => {
  const normalized = normalizeCountyName(value);
  if (!normalized) return 'County name is required';
  const isDuplicate = counties.some((county, i) => i !== index && normalizeCountyName(county.name) === normalized);
  return !isDuplicate || 'Duplicate county';
};
